/**
 * Mission Config Slice
 *
 * GW-5445 (P1-10) / GW-5448 (P2-3): Zustand slice that holds the
 * mission-config payload and exposes fetch/clear actions.
 *
 * - Fixture mode (`runtimeConfig.missionsFixtureMode === true`): returns the
 *   local fixture.
 * - Live mode (default): calls `missionConfigApi.getConfig()` against
 *   `GET /v1/missions/config` on the gw-missions Lambda.
 */

import { missionConfigApi } from '@/api/missionConfig';
import { getConfig } from '@/config/runtimeConfig';
import { fixtureMissionConfig } from '@/mocks/missions/fixtureMissionConfig';
import type { MissionConfigPayload } from '@/types/missionConfig.types';

import type { StateCreator } from 'zustand';

type MissionConfigStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface MissionConfigSlice {
  missionConfig: MissionConfigPayload | null;
  missionConfigStatus: MissionConfigStatus;
  missionConfigLastFetched: number | null;
  missionConfigError: string | null;
  fetchMissionConfig: () => Promise<void>;
  clearMissionConfig: () => void;
}

const initial: Pick<
  MissionConfigSlice,
  'missionConfig' | 'missionConfigStatus' | 'missionConfigLastFetched' | 'missionConfigError'
> = {
  missionConfig: null,
  missionConfigStatus: 'idle',
  missionConfigLastFetched: null,
  missionConfigError: null,
};

export const createMissionConfigSlice: StateCreator<
  MissionConfigSlice,
  [],
  [],
  MissionConfigSlice
> = (set) => ({
  ...initial,
  fetchMissionConfig: async () => {
    set({ missionConfigStatus: 'loading', missionConfigError: null });
    // Yield to the microtask queue so consumers observe the loading state
    // before the payload resolves. Mirrors the async behaviour of a real
    // AppConfig fetch and keeps the fixture path realistic for tests.
    await Promise.resolve();
    if (getConfig().missionsFixtureMode) {
      set({
        missionConfig: fixtureMissionConfig,
        missionConfigStatus: 'ready',
        missionConfigLastFetched: Date.now(),
        missionConfigError: null,
      });
      return;
    }
    try {
      const config = await missionConfigApi.getConfig();
      set({
        missionConfig: config,
        missionConfigStatus: 'ready',
        missionConfigLastFetched: Date.now(),
        missionConfigError: null,
      });
    } catch (err) {
      set({
        missionConfig: null,
        missionConfigStatus: 'error',
        missionConfigError: err instanceof Error ? err.message : 'Failed to load mission config',
      });
    }
  },
  clearMissionConfig: () => set({ ...initial }),
});
